import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import StarField from '../components/StarField';
import PhoneFrame from '../components/PhoneFrame';
import { GalaxyMark } from '../components/OrbitLogo';
import { SCREEN_TIME } from '../mock';
import { ArrowLeft, Bell, X, Moon, Star, ChevronRight, Shield, Lock } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const TABS = ['Privacy', 'Safety', 'Screen Time'];
const LIMITS = [{ l: '1h', m: 60 }, { l: '2h', m: 120 }, { l: '3h', m: 180 }, { l: '4h', m: 240 }, { l: 'Custom', m: 0 }];

export default function Settings() {
  const nav = useNavigate();
  const { screenLimit, setScreenLimit, usedMinutes, resetInMs } = useApp();
  const [tab, setTab] = useState('Screen Time');
  const [banner, setBanner] = useState(true);
  const [breaks, setBreaks] = useState(true);
  const [limitUsage, setLimitUsage] = useState(true);

  const used = usedMinutes;
  const pct = Math.min(100, Math.round((used / (screenLimit || 240)) * 100));
  const hm = (m) => `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
  const resetHours = Math.floor(Math.max(0, resetInMs) / 3600000);
  const resetMinutes = Math.floor((Math.max(0, resetInMs) % 3600000) / 60000);
  const resetCountdown = `${resetHours}h ${resetMinutes}m`;

  return (
    <PhoneFrame>
      <div className="relative min-h-full">
        <StarField />
        <div className="relative z-10 pb-12">
          {/* Header */}
          <div className="orbit-page-header justify-between">
            <button onClick={() => nav(-1)} className="press orbit-icon-button" aria-label="Go back"><ArrowLeft /></button>
            <h1 className="font-display orbit-page-title">Settings</h1>
            <div className="floaty"><GalaxyMark size={40} /></div>
          </div>

          {/* Tabs */}
          <div className="flex justify-between px-6 mt-2">
            {TABS.map((t) => (
              <button key={t} onClick={() => setTab(t)} className="press relative pb-2 font-display font-bold"
                style={{ fontSize: 18, color: tab === t ? '#f3ecff' : '#6f6795' }}>
                <span className={tab === t ? 'text-glow-purple' : ''}>{t}</span>
                {tab === t && <span className="absolute left-0 right-0 -bottom-0 h-[3px] rounded-full glow-pink" style={{ background: '#b98cff' }} />}
              </button>
            ))}
          </div>
          <div className="h-px w-full mt-1" style={{ background: 'linear-gradient(90deg,transparent,rgba(201,162,75,.5),transparent)' }} />

          {tab === 'Screen Time' && (
            <div className="px-5">
              {/* Usage */}
              <div className="flex gap-4 mt-5">
                <div className="shrink-0"><GalaxyMark size={92} /></div>
                <div className="flex-1">
                  <div className="font-semibold" style={{ color: '#e7dcff' }}>Today's Screen Time</div>
                  <div className="font-display font-extrabold text-glow-purple" style={{ fontSize: 40, color: '#efe4ff', lineHeight: 1 }}>{hm(used)}</div>
                  <div className="text-sm" style={{ color: '#a89fc9' }}>of {hm(screenLimit)} limit</div>
                  <div className="neon-track mt-2 rounded-full overflow-hidden" style={{ height: 16, padding: 2 }}>
                    <div className="neon-fill h-full rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-sm mt-2" style={{ color: '#c9bcf0' }}>You're doing great! Keep it balanced.</div>
                </div>
              </div>

              {banner && (
                <div className="flex items-center gap-3 mt-4 p-3 rounded-2xl" style={{ background: 'rgba(108,95,191,.18)', border: '1px solid rgba(139,124,232,.3)' }}>
                  <span className="flex items-center justify-center rounded-full shrink-0" style={{ width: 40, height: 40, background: '#5a4db0' }}><Bell size={18} color="#fff" /></span>
                  <p className="italic text-sm flex-1" style={{ color: '#c9bcf0' }}>Orbit will remind you to take a break and pause when your limit is reached.</p>
                  <button onClick={() => setBanner(false)} className="press" style={{ color: '#b7a5ff' }}><X size={20} /></button>
                </div>
              )}

              <div className="relative flex items-center my-5">
                <div className="star-line flex-1" /><Star size={22} fill="#c4b0ff" color="#c4b0ff" className="mx-2" /><div className="star-line flex-1" />
              </div>

              {/* Limit selector */}
              <div className="orbit-surface-card">
                <div className="font-display font-bold mb-3" style={{ color: '#f3ecff', fontSize: 18 }}>Set Daily Screen Time Limit</div>
                <div className="flex rounded-xl overflow-hidden" style={{ border: '1px solid rgba(183,165,255,.4)' }}>
                  {LIMITS.map((x, i) => {
                    const activeSel = x.m === screenLimit || (x.l === 'Custom' && ![60,120,180,240].includes(screenLimit));
                    return (
                      <button key={x.l} onClick={() => { if (x.m) setScreenLimit(x.m); else { const c = prompt('Custom limit in minutes'); if (c) setScreenLimit(Number(c)); } }}
                        className="press flex-1 py-3 font-display font-bold" style={{
                          fontSize: 15, color: activeSel ? '#fff' : '#c9bcf0',
                          background: activeSel ? 'linear-gradient(180deg,#6f4fc6,#4a2f9c)' : 'rgba(30,34,68,.6)',
                          borderLeft: i ? '1px solid rgba(183,165,255,.25)' : 'none' }}>{x.l}</button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2 mt-3 text-sm"><Moon size={16} color="#c9a24b" /><span style={{ color: '#a89fc9' }}>Limit resets in <b style={{ color: '#ff9de0' }}>{resetCountdown}</b></span></div>
              </div>

              {/* Break reminders */}
              <div className="orbit-surface-card mt-4">
                <div className="flex items-center justify-between">
                  <div className="font-display font-bold" style={{ color: '#f3ecff', fontSize: 18 }}>Mandatory Break Reminders</div>
                  <Switch checked={breaks} onCheckedChange={setBreaks} />
                </div>
                <p className="italic text-sm mt-1" style={{ color: '#a89fc9' }}>Get reminded before Orbit closes to help you rest.</p>
                <div className="h-px my-3" style={{ background: 'rgba(183,165,255,.2)' }} />
                <button onClick={() => toast('Reminder timing options')} className="press w-full flex items-center justify-between">
                  <span className="font-semibold" style={{ color: '#e7dcff' }}>Remind me</span>
                  <span className="flex items-center gap-1 text-sm" style={{ color: '#c9bcf0' }}>{SCREEN_TIME.remindBefore} <ChevronRight size={16} /></span>
                </button>
              </div>

              {/* Limit usage list */}
              <div className="orbit-surface-card mt-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="font-display font-bold" style={{ color: '#f3ecff', fontSize: 18 }}>Limit Usage</div>
                  <Switch checked={limitUsage} onCheckedChange={setLimitUsage} />
                </div>
                {[['Downtime', 'Schedule time away from the screen'], ['App Limits', 'Set time limits for apps'], ['Always Allowed', 'Choose apps to allow at all times'], ['Screen Distance', 'Reduce eye strain']].map(([t, s], i) => (
                  <button key={t} onClick={() => toast(t)} className="press w-full text-left py-2" style={{ opacity: limitUsage ? 1 : 0.4, borderTop: i ? '1px solid rgba(183,165,255,.15)' : 'none' }}>
                    <div className="font-semibold" style={{ color: '#e7dcff' }}>{t}</div>
                    <div className="text-sm" style={{ color: '#b7a5ff' }}>{s}</div>
                  </button>
                ))}
              </div>

              <button onClick={() => toast('Break started — see you soon 🌙')} className="press orbit-secondary-button w-full mt-6 font-display">Take a Break</button>
            </div>
          )}

          {tab !== 'Screen Time' && (
            <div className="px-5 mt-6 space-y-3">
              {(tab === 'Privacy'
                ? [['Private Orbit', 'Only approved friends can see your broadcasts', Lock], ['Hide Activity', 'Turn off your online status', Shield], ['Blocked Signals', 'Manage who you\u2019ve muted', X]]
                : [['Two-Star Verification', 'Extra security on launch', Shield], ['Sensitive Content', 'Blur art that may be flagged', Lock], ['Report Nebula', 'Review reports you\u2019ve sent', Bell]]
              ).map(([t, s, Ic]) => (
                <button key={t} onClick={() => toast(t)} className="press orbit-surface-card w-full flex items-center gap-3 text-left">
                  <span className="flex items-center justify-center rounded-full" style={{ width: 40, height: 40, background: 'rgba(90,77,176,.5)', color: '#cdb8ff' }}><Ic size={18} /></span>
                  <div className="flex-1"><div className="font-semibold" style={{ color: '#e7dcff' }}>{t}</div><div className="text-sm" style={{ color: '#a89fc9' }}>{s}</div></div>
                  <ChevronRight size={18} style={{ color: '#6f6795' }} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </PhoneFrame>
  );
}
